import { TFunction } from "i18next";
import { createRoot } from "react-dom/client";
import toast from "react-hot-toast";
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import OfficeAccessAuthorizationPermitPDF from "../components/permitPDFs/OfficeAccessAuthorizationPermitPDF";
import { PERMIT_TYPES } from "../constants/common";
import PCInOutPermitPDF from "../components/permitPDFs/PCInOutPermitPDF";
import LostVehicleStickerIDCardPermitPDF from "../components/permitPDFs/LostVehicleStickerIDCardPermitPDF";
import IssuanceRenewalIdPermitPDF from "../components/permitPDFs/IssuanceRenewalIdPermitPDF";
import ParkingStickerRequestPermitPDF from "../components/permitPDFs/ParkingStickerRequestPermitPDF";
import WarehouseInOutPermitPDF from "../components/permitPDFs/WarehouseInOutPermitPDF";
import PermitToWorkPDF from "../components/permitPDFs/PermitToWorkPDF";

// Function to generate PDF as File object from permit form
export const generatePermitPDFAsFile = async (
  type: PERMIT_TYPES,
  permitDetails: any,
  fileName = "permit.pdf",
  t: TFunction<"translation", undefined>,
): Promise<File | null> => {
  try {
    // Create a temporary container for the permit form
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "fixed";
    tempContainer.style.top = "-9999px";
    tempContainer.style.left = "-9999px";
    tempContainer.style.bottom = "1100px";
    tempContainer.style.width = "794px"; // A4 width in pixels (210mm)
    tempContainer.style.height = "1100px";
    tempContainer.style.backgroundColor = "white";
    tempContainer.style.color = "black";
    tempContainer.style.zIndex = "-1000";
    tempContainer.id = "temp-permit-container";

    // Add all the required styles for the content
    tempContainer.style.fontFamily = "Arial, sans-serif";
    tempContainer.style.fontSize = "10px";
    tempContainer.style.lineHeight = "1.4";

    document.body.appendChild(tempContainer);

    // Create a root for React rendering
    const root = createRoot(tempContainer);

    // Render the PermitForm component with a Promise that resolves when rendering is complete
    await new Promise<void>((resolve) => {
      try {
        root.render(
          <div
            style={{
              backgroundColor: "white",
              color: "black",
              width: "100%",
              fontFamily: "Arial, sans-serif",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            {type === PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION ?
              <OfficeAccessAuthorizationPermitPDF permitDetails={permitDetails} />
              : type === PERMIT_TYPES.PC_IN_OUT_PERMIT ?
                <PCInOutPermitPDF permitDetails={permitDetails} />
                : type === PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD ?
                  <LostVehicleStickerIDCardPermitPDF permitDetails={permitDetails} />
                  : type === PERMIT_TYPES.ISSUANCE_RENEWAL_ID ?
                    <IssuanceRenewalIdPermitPDF permitDetails={permitDetails} />
                    : type === PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT ?
                      <WarehouseInOutPermitPDF permitDetails={permitDetails} />
                      : type === PERMIT_TYPES.PERMIT_TO_WORK ?
                        <PermitToWorkPDF permitDetails={permitDetails} />
                        : type === PERMIT_TYPES.PARKING_STICKER_REQUEST ?
                          <ParkingStickerRequestPermitPDF permitDetails={permitDetails} />
                          : <></>
            }
          </div>,
        );
      } catch (error: any) {
        toast.error(error.message || t("somethingWentWrong"));
        resolve();
      }

      // Wait longer for rendering and any dynamic content to load
      setTimeout(() => {
        // Check if content was actually rendered
        const contentCheck = tempContainer.querySelector("h1, h2, table, p");
        if (contentCheck) {
          resolve();
        } else {
          console.warn("Content not found, retrying...");
          setTimeout(resolve, 500); // Give more time if needed
        }
      }, 500);
    });

    // Generate canvas from the permit form with better options
    const canvas = await html2canvas(tempContainer, {
      scale: 1.5,
      useCORS: true,
      logging: true, // Enable logging for debugging
      backgroundColor: "#ffffff",
      allowTaint: true,
      foreignObjectRendering: false, // Disable this as it can cause issues
      removeContainer: false,
      width: tempContainer.scrollWidth,
      height: tempContainer.scrollHeight,
      windowWidth: tempContainer.scrollWidth,
      windowHeight: tempContainer.scrollHeight,
    });

    // Unmount React component
    root.unmount();

    // Create PDF
    const pdf = new jsPDF({
      orientation: "p",
      unit: "px",
      format: "a4",
    });

    const imgData = canvas.toDataURL("image/png");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // If content is too tall, we might need multiple pages
    if (pdfHeight > pdf.internal.pageSize.getHeight()) {
      const pageHeight = pdf.internal.pageSize.getHeight();
      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }
    } else {
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    // Get PDF as blob
    const pdfBlob = pdf.output("blob");

    // Convert blob to File
    const pdfFile = new File([pdfBlob], fileName, {
      type: "application/pdf",
      lastModified: Date.now(),
    });

    // Clean up temporary elements
    document.body.removeChild(tempContainer);

    return pdfFile;
  } catch (error: any) {
    toast.error(t("failedToGeneratePDF"));
    // Make sure to clean up in case of error
    const tempContainer = document.getElementById("temp-permit-container");
    if (tempContainer) {
      document.body.removeChild(tempContainer);
    }
    return null;
  }
};
